import type { ArcEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

type ArcRow = ArcEvent & { pageId?: string };

function hasCorrectionFields(row: ArcRow): boolean {
  return (
    row.certificateKind === 'correction' ||
    (row.correctsFormTrackingNumber != null && row.correctsFormTrackingNumber !== '') ||
    (row.correctsDate != null && row.correctsDate !== '')
  );
}

export function ARCTable({
  arcEvents,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  arcEvents: ArcRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: ArcRow, index: number) => void;
  rowFindings?: RowFindings;
}) {
  const showCorrection = arcEvents.some(hasCorrectionFields);

  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>Part No</Th>
          <Th>Serial</Th>
          <Th>Qty</Th>
          <Th>Description</Th>
          <Th>Status</Th>
          <Th>TSN</Th>
          <Th>CSN</Th>
          <Th>Date</Th>
          <Th>Authority</Th>
          <Th>Form Type</Th>
          <Th>Approval #</Th>
          <Th>Form Tracking #</Th>
          {showCorrection && <Th>Kind</Th>}
          {showCorrection && <Th>Corrects Tracking #</Th>}
          {showCorrection && <Th>Corrects Date</Th>}
          <Th>Work Order</Th>
          <Th>Org</Th>
          <Th>ex ESN</Th>
          <Th>Cert</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {arcEvents.map((r, i) => (
          <tr
            key={`${r.pageId}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePageCell pageId={r.pageId} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td className="font-mono">{r.partNumber || '—'}</Td>
            <Td className="font-mono">{r.serialNumber ?? '—'}</Td>
            <Td>{r.quantity ?? '—'}</Td>
            <TruncatedTd value={r.description} maxWidth="max-w-[240px]" />
            <Td>
              <StatusBadge status={r.status} statusRaw={r.statusRaw} />
            </Td>
            <Td>{r.tsn ?? '—'}</Td>
            <Td>{r.csn ?? '—'}</Td>
            <Td>{r.date ?? '—'}</Td>
            <Td>{r.authority ?? '—'}</Td>
            <Td>{r.formType ?? '—'}</Td>
            <Td className="font-mono">{r.approvalNumber ?? '—'}</Td>
            <Td className="font-mono">{r.formTrackingNumber ?? '—'}</Td>
            {showCorrection && (
              <Td>{r.certificateKind === 'correction' ? 'Correction' : '—'}</Td>
            )}
            {showCorrection && (
              <Td className="font-mono">{r.correctsFormTrackingNumber ?? '—'}</Td>
            )}
            {showCorrection && <Td>{r.correctsDate ?? '—'}</Td>}
            <Td className="font-mono">{r.workOrder ?? '—'}</Td>
            <TruncatedTd value={r.organizationName} maxWidth="max-w-[200px]" />
            <Td className="font-mono">{r.exESN ?? '—'}</Td>
            <Td>
              <CertBadges row={r} />
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Badge tone per normalised status/work code. A Record (not a ternary chain) so a future enum
 * member can't quietly fall through to the neutral tone — TypeScript demands an entry for it.
 */
const STATUS_TONES: Record<NonNullable<ArcRow['status']>, string> = {
  NEW: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200',
  OH: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
  RP: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
  MODIFIED: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
  INSP: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200',
  UNS: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200',
  PROTOTYPE: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200',
  AR: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200',
  USED: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200',
};

function StatusBadge({ status, statusRaw }: { status: ArcRow['status']; statusRaw: string | undefined }) {
  // DR-2876: a printed status outside the enum keeps only its verbatim text — show that rather
  // than an em dash, so the cell still tells the reader what the certificate says.
  // Clipped like the table's free-text columns (`TruncatedTd`) — a long verbatim cell must not
  // push the columns beside it off-screen; the full text stays in the hover title.
  if (!status) {
    return statusRaw ? (
      <span className="block max-w-[140px] truncate text-gray-500 dark:text-zinc-400" title={statusRaw}>
        {statusRaw}
      </span>
    ) : (
      <>—</>
    );
  }
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${STATUS_TONES[status]}`}
      title={statusRaw && statusRaw !== status ? `Raw: ${statusRaw}` : undefined}
    >
      {status}
    </span>
  );
}

function CertBadges({ row }: { row: ArcRow }) {
  const badges: React.ReactNode[] = [];
  if (row.isFaaCertified) badges.push(<Chip key="faa" label="FAA" />);
  if (row.isEasaCertified) badges.push(<Chip key="easa" label="EASA" />);
  if (row.isSigned) badges.push(<Chip key="signed" label="Signed" tone="green" />);
  if (badges.length === 0) return <>—</>;
  return <span className="flex flex-wrap gap-0.5">{badges}</span>;
}

function Chip({ label, tone = 'neutral' }: { label: string; tone?: 'neutral' | 'green' }) {
  const cls =
    tone === 'green'
      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
      : 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200';
  return <span className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${cls}`}>{label}</span>;
}
